import { useEffect, useState, useRef } from 'react';
import { 
  Brain, 
  Layers, 
  Repeat, 
  Globe, 
  Cloud, 
  Package, 
  MapPin,
  Users
} from 'lucide-react';

const differentiators = [
  {
    icon: Brain,
    title: 'AI-First Thinking',
    description: 'Solutions designed with AI embedded from day one, not bolted on as an afterthought.',
  },
  {
    icon: Layers,
    title: 'End-to-End Capability',
    description: 'Full technology lifecycle management from strategy to support, reducing vendor complexity.',
  },
  {
    icon: Repeat,
    title: 'Proven Agile Delivery',
    description: '2-week sprints with 40-50 story points velocity ensuring predictable, quality delivery.',
  },
  {
    icon: Globe,
    title: 'Cross-Industry Experience',
    description: 'Expertise across healthcare, fintech, real estate, manufacturing, media, and drone services.',
  },
  {
    icon: Cloud,
    title: 'Scalable Cloud Solutions',
    description: 'Built on Azure/AWS for seamless growth without costly re-engineering.',
  },
  {
    icon: Package,
    title: 'Multi-Product Portfolio',
    description: 'Both services and enterprise-grade pre-built products for faster time-to-value.',
  },
  {
    icon: MapPin,
    title: 'Local Presence, Global Standards',
    description: 'Malaysian market understanding combined with international quality and security standards.',
  },
  {
    icon: Users,
    title: 'Senior Leadership Engagement',
    description: 'Co-Founders personally involved in client engagements for strategic alignment.',
  },
];

export default function WhyHireSphereSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why"
      ref={sectionRef}
      data-testid="why-section"
      className="py-24 sm:py-32 bg-[#F4F7FD]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#F5A623] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
            Why Choose Us
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-[#1A1A2E]">
            The HireSphere Advantage
          </h2>
        </div>

        {/* Differentiators Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {differentiators.map((item, index) => (
            <div
              key={item.title}
              data-testid={`differentiator-card-${index}`}
              className={`glass-card-light rounded-2xl p-6 reveal ${
                isVisible ? 'visible' : ''
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className="w-12 h-12 rounded-xl bg-[#193E82]/10 flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-[#193E82]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A1A2E] mb-2">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-[#5A5A5A]">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
